import React, { useMemo, useState, ReactNode, useEffect, useContext, useCallback, createContext } from 'react';

import { useAuthContext } from 'src/auth/hooks';
import { useDjangoApi } from 'src/universal-components/djangoServerUtilities/djangoHandler';

export interface CountsState {
  [key: string]: number;
}

export interface CountsContextType {
  counts: CountsState;
  refreshCount: (key: string) => Promise<void>;
  refreshAllCounts: () => Promise<void>;
  getCount: (key: string) => number;
  setCount: (key: string, value: number) => void;
}

// you can add more keys here
const COUNT_KEYS = {
  PENDING_CONSULTATIONS: 'pendingConsultations',
} as const;

// Context
const CountsContext = createContext<CountsContextType | undefined>(undefined);

// Provider Props
interface CountsProviderProps {
  children: ReactNode;
}

// Provider Component (not like a clinical provider, but like a provider of counts)
export function CountsProvider({ children }: CountsProviderProps) {
  const djangoApi = useDjangoApi();
  const { user } = useAuthContext();
  const [counts, setCounts] = useState<CountsState>({});

  const isProviderUser = Boolean(
    user?.providerId || user?.roles?.includes('Provider')
  );

  // Get current count value
  const getCount = useCallback((key: string): number => counts[key] || 0, [counts]);

  // Manually set the current count value
  const setCount = useCallback((key: string, value: number) => {
    setCounts(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Refresh a specific count
  const refreshCount = useCallback(async (key: string) => {
    try {
      switch (key) {
        case COUNT_KEYS.PENDING_CONSULTATIONS: {
          if (!isProviderUser) {
            setCounts(prev => ({
              ...prev,
              [key]: 0,
            }));
            break;
          }

          const response = await djangoApi.get('providers/consultations/', { count_only: 'true' });
          if (response && response.status === 200) {
            setCounts(prev => ({
              ...prev,
              [key]: response.data.count,
            }));
          } else {
            console.error(`Failed to refresh count for ${key}:`, response);
          }
          break;
        }
        default:
          console.warn(`Unknown count key: ${key}`);
      }
    } catch (error) {
      console.error(`Failed to refresh count for ${key}:`, error);
      setCounts(prev => ({
        ...prev,
        [key]: 0,
      }));
    }
  }, [djangoApi, isProviderUser]);

  // Refresh all counts
  const refreshAllCounts = useCallback(async () => {
    const refreshPromises = Object.values(COUNT_KEYS).map(key => refreshCount(key));
    await Promise.allSettled(refreshPromises);
  }, [refreshCount]);

  // Fetch all counts on initial load (provider-only counts skip non-provider users)
  useEffect(() => {
    if (!user) {
      return;
    }
    refreshAllCounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.providerId, user?.roles]);

  const contextValue = useMemo<CountsContextType>(() => ({
    counts,
    refreshCount,
    refreshAllCounts,
    getCount,
    setCount,
  }), [counts, refreshCount, refreshAllCounts, getCount, setCount]);

  return (
    <CountsContext.Provider value={contextValue}>
      {children}
    </CountsContext.Provider>
  );
}

// Hook to use the context
export function useCounts(): CountsContextType {
  const context = useContext(CountsContext);
  if (context === undefined) {
    throw new Error('useCounts must be used within a CountsProvider');
  }
  return context;
}
